import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { I18nProvider, useI18n } from "@/locales/i18n-provider";
import { isSessionExpiredError } from "@/request/request-client/session-expired";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: (failureCount, error) => !isSessionExpiredError(error) && failureCount < 3,
    },
  },
});

/** ABP 成功响应也可能含本地化文本；切换语言后让活跃查询重新获取。 */
function LocaleQuerySynchronizer() {
  const client = useQueryClient();
  const { locale } = useI18n();
  const previousLocale = useRef(locale);

  useEffect(() => {
    if (previousLocale.current === locale) {
      return;
    }
    previousLocale.current = locale;
    void client.cancelQueries().then(() => client.invalidateQueries());
  }, [client, locale]);

  return null;
}

/** 两套 surface 共同使用的无业务语义基础设施。 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <I18nProvider>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <LocaleQuerySynchronizer />
          {children}
        </QueryClientProvider>
      </HelmetProvider>
    </I18nProvider>
  );
}
